import { useEffect, type ReactNode } from 'react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  maxWidth?: string;
}

export default function Modal({ open, onClose, title, subtitle, children, maxWidth = 'max-w-2xl' }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`relative w-full ${maxWidth} max-h-[90vh] overflow-y-auto scrollbar-thin rounded-2xl bg-background-50 shadow-xl ring-1 ring-background-200`}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-background-200 bg-background-50 px-6 py-5">
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground-950">{title}</h3>
            {subtitle && <p className="mt-1 text-sm text-foreground-600">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-600 transition hover:bg-background-200 hover:text-foreground-950 cursor-pointer"
            aria-label="Kapat"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="px-6 py-6">{children}</div>
      </div>
    </div>
  );
}